// HeaderProfileMenu.js - Avatar menu for the redesigned header: who-you-are
// header, Preferences (language, curriculum, dark mode, zoom), Tools
// (upgrade/admin/scanner, compact-only), Help (compact-only), then
// Profile / Log out.

import React, { useState } from "react";
import { Pressable, Switch, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import HeaderDropdown, {
  HeaderMenuDivider,
  HeaderMenuItem,
  HeaderMenuSection,
} from "./HeaderDropdown";
import { HEADER, initialsFor, menuColors } from "./headerTheme";

export default function HeaderProfileMenu({
  user,
  isDarkMode,
  compact,
  t = (s) => s,
  language,
  onLanguage,
  curriculum,
  onCurriculum,
  theme,
  zoom,
  showUpgrade,
  onUpgrade,
  showAdmin,
  onAdmin,
  showScanner,
  onScanner,
  showHelpItems,
  onAppGuide,
  onSupport,
  onProfile,
  onLogout,
}) {
  const [open, setOpen] = useState(false);
  const colors = menuColors(isDarkMode);
  const close = () => setOpen(false);
  const withClose = (handler) => () => {
    if (handler) handler();
    close();
  };

  const name = user && user.name;
  const hasName = typeof name === "string" && name.trim().length > 0;
  const firstName = hasName ? name.trim().split(/\s+/)[0] : t("Guest");
  const whoSecondLine = [user && user.roleLabel, user && user.email]
    .filter(Boolean)
    .join(" · ");

  const showTools = (compact && showUpgrade) || showAdmin || showScanner;
  const showHelp = compact && showHelpItems;
  const zoomAtMin = !!zoom && zoom.level <= zoom.min;
  const zoomAtMax = !!zoom && zoom.level >= zoom.max;

  const trigger = (
    <Pressable
      testID="profile-menu-trigger"
      onPress={() => setOpen((prev) => !prev)}
      accessibilityRole="button"
      accessibilityLabel={t("Account menu")}
      accessibilityState={{ expanded: open }}
      style={({ hovered }) => [styles.trigger, hovered && styles.triggerHover]}
    >
      <View style={styles.triggerInitials}>
        <Text style={styles.triggerInitialsText}>{initialsFor(name)}</Text>
      </View>
      {!compact && <Text style={styles.triggerName}>{firstName}</Text>}
      <MaterialCommunityIcons
        name={open ? "chevron-up" : "chevron-down"}
        size={18}
        color={HEADER.onBrand}
      />
    </Pressable>
  );

  return (
    <HeaderDropdown
      open={open}
      onClose={close}
      isDarkMode={isDarkMode}
      align="right"
      width={280}
      trigger={trigger}
      testID="profile-menu"
    >
      <View style={styles.whoRow}>
        <View style={styles.whoInitials}>
          <Text style={styles.whoInitialsText}>{initialsFor(name)}</Text>
        </View>
        <View style={styles.whoText}>
          <Text style={[styles.whoName, { color: colors.text }]}>{name || t("Guest")}</Text>
          {!!whoSecondLine && (
            <Text style={[styles.whoMeta, { color: colors.muted }]}>{whoSecondLine}</Text>
          )}
        </View>
      </View>

      <HeaderMenuDivider isDarkMode={isDarkMode} />
      <HeaderMenuSection isDarkMode={isDarkMode} title={t("Preferences")} />

      <HeaderMenuItem
        isDarkMode={isDarkMode}
        icon="translate"
        label={t("Language")}
        onPress={withClose(onLanguage)}
        testID="profile-menu-language"
        right={
          <View style={styles.rightRow}>
            <Text style={[styles.rightLabel, { color: colors.muted }]}>
              {language && language.label}
            </Text>
            <MaterialCommunityIcons name="chevron-right" size={18} color={colors.muted} />
          </View>
        }
      />
      <HeaderMenuItem
        isDarkMode={isDarkMode}
        icon="school-outline"
        label={t("Curriculum")}
        onPress={withClose(onCurriculum)}
        testID="profile-menu-curriculum"
        right={
          <View style={styles.rightRow}>
            <Text style={[styles.rightLabel, { color: colors.muted }]}>
              {curriculum && curriculum.label}
            </Text>
            <MaterialCommunityIcons name="chevron-right" size={18} color={colors.muted} />
          </View>
        }
      />
      <HeaderMenuItem
        isDarkMode={isDarkMode}
        icon="weather-night"
        label={t("Dark mode")}
        onPress={() => theme && theme.onToggle && theme.onToggle(!theme.isDark)}
        right={
          // Wrapped so the Switch's own native onValueChange path is
          // disconnected — the row's onPress above is the single path that
          // calls theme.onToggle.
          <View pointerEvents="none">
            <Switch
              testID="profile-menu-theme"
              value={!!(theme && theme.isDark)}
              trackColor={{ true: HEADER.brand }}
            />
          </View>
        }
      />
      {zoom && (
        // Plain View row, not a HeaderMenuItem/menuitem: the row itself is
        // not a single action, only the −/reset/+ controls inside it are.
        <View testID="profile-menu-zoom-row" style={styles.zoomItemRow}>
          <MaterialCommunityIcons name="magnify-plus-outline" size={20} color={colors.muted} />
          <Text style={[styles.zoomItemLabel, { color: colors.text }]}>{t("Zoom")}</Text>
          <View style={styles.zoomRow}>
            <Pressable
              testID="profile-menu-zoom-out"
              onPress={zoomAtMin ? undefined : zoom.onOut}
              disabled={zoomAtMin}
              accessibilityRole="button"
              accessibilityLabel={t("Zoom out")}
              style={[styles.zoomBtn, zoomAtMin && styles.zoomBtnDisabled]}
            >
              <Text style={[styles.zoomBtnText, { color: colors.text }]}>{"−"}</Text>
            </Pressable>
            <Pressable
              testID="profile-menu-zoom-reset"
              onPress={zoom.onReset}
              accessibilityLabel={t("Reset zoom")}
            >
              <Text style={[styles.zoomPct, { color: colors.text }]}>{zoom.level}%</Text>
            </Pressable>
            <Pressable
              testID="profile-menu-zoom-in"
              onPress={zoomAtMax ? undefined : zoom.onIn}
              disabled={zoomAtMax}
              accessibilityRole="button"
              accessibilityLabel={t("Zoom in")}
              style={[styles.zoomBtn, zoomAtMax && styles.zoomBtnDisabled]}
            >
              <Text style={[styles.zoomBtnText, { color: colors.text }]}>+</Text>
            </Pressable>
          </View>
        </View>
      )}

      {showTools && (
        <>
          <HeaderMenuSection isDarkMode={isDarkMode} title={t("Tools")} />
          {compact && showUpgrade && (
            <HeaderMenuItem
              isDarkMode={isDarkMode}
              icon="credit-card-outline"
              label={t("Upgrade plan")}
              onPress={withClose(onUpgrade)}
              testID="profile-menu-upgrade"
            />
          )}
          {showAdmin && (
            <HeaderMenuItem
              isDarkMode={isDarkMode}
              icon="shield-account"
              label={t("Admin panel")}
              tag={t("admin")}
              onPress={withClose(onAdmin)}
              testID="profile-menu-admin"
            />
          )}
          {showScanner && (
            <HeaderMenuItem
              isDarkMode={isDarkMode}
              icon="qrcode-scan"
              label={t("Da Vinci scanner")}
              onPress={withClose(onScanner)}
              testID="profile-menu-scanner"
            />
          )}
        </>
      )}

      {showHelp && (
        <>
          <HeaderMenuSection isDarkMode={isDarkMode} title={t("Help")} />
          <HeaderMenuItem
            isDarkMode={isDarkMode}
            icon="book-open-page-variant"
            label={t("App guide")}
            onPress={withClose(onAppGuide)}
            testID="profile-menu-app-guide"
          />
          <HeaderMenuItem
            isDarkMode={isDarkMode}
            icon="lifebuoy"
            label={t("Support")}
            onPress={withClose(onSupport)}
            testID="profile-menu-support"
          />
        </>
      )}

      <HeaderMenuDivider isDarkMode={isDarkMode} />
      <HeaderMenuItem
        isDarkMode={isDarkMode}
        icon="account-outline"
        label={t("Profile")}
        onPress={withClose(onProfile)}
        testID="profile-menu-profile"
      />
      <HeaderMenuItem
        isDarkMode={isDarkMode}
        icon="logout"
        label={t("Log out")}
        danger
        onPress={withClose(onLogout)}
        testID="profile-menu-logout"
      />
    </HeaderDropdown>
  );
}

const styles = {
  trigger: {
    height: 36,
    borderRadius: 999,
    paddingLeft: 4,
    paddingRight: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  triggerHover: {
    backgroundColor: HEADER.onBrandHover,
  },
  triggerInitials: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: HEADER.initialsBg,
    alignItems: "center",
    justifyContent: "center",
  },
  triggerInitialsText: {
    color: HEADER.brandDeep,
    fontSize: 12,
    fontWeight: "700",
  },
  triggerName: {
    color: HEADER.onBrand,
    fontSize: 14,
    fontWeight: "600",
  },
  whoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 8,
  },
  whoInitials: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: HEADER.brand,
    alignItems: "center",
    justifyContent: "center",
  },
  whoInitialsText: {
    color: HEADER.onBrand,
    fontSize: 14,
    fontWeight: "700",
  },
  whoText: {
    flexShrink: 1,
  },
  whoName: {
    fontSize: 15,
    fontWeight: "700",
  },
  whoMeta: {
    fontSize: 12.5,
    marginTop: 2,
  },
  rightRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  rightLabel: {
    fontSize: 13,
  },
  zoomItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  zoomItemLabel: {
    fontSize: 14,
    flexGrow: 1,
  },
  zoomRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: "auto",
  },
  zoomBtn: {
    width: 26,
    height: 26,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  zoomBtnDisabled: {
    opacity: 0.4,
  },
  zoomBtnText: {
    fontSize: 15,
    fontWeight: "600",
  },
  zoomPct: {
    fontSize: 12.5,
    fontVariant: ["tabular-nums"],
    minWidth: 34,
    textAlign: "center",
  },
};
